import { captureError, addBreadcrumb } from './sentry'
import { auth } from './firebase'

interface RetryOptions {
  maxRetries?: number
  baseDelayMs?: number
  maxDelayMs?: number
  retryableStatuses?: number[]
}

const DEFAULT_RETRY_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
  retryableStatuses: [408, 429, 500, 502, 503, 504],
}

/**
 * Sleep for a given number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Calculate delay with exponential backoff and jitter
 */
function calculateDelay(attempt: number, baseDelay: number, maxDelay: number): number {
  const exponentialDelay = baseDelay * Math.pow(2, attempt)
  const jitter = Math.random() * 0.3 * exponentialDelay // Add up to 30% jitter
  return Math.min(exponentialDelay + jitter, maxDelay)
}

/**
 * Check if an error is retryable
 */
function isRetryableError(error: unknown): boolean {
  if (error instanceof TypeError) {
    // Network errors (failed to fetch)
    return true
  }
  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    return (
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('fetch') ||
      message.includes('econnreset') ||
      message.includes('enotfound')
    )
  }
  return false
}

/**
 * Fetch with automatic retry and exponential backoff
 */
export async function fetchWithRetry(
  url: string,
  options?: RequestInit,
  retryOptions?: RetryOptions
): Promise<Response> {
  const opts = { ...DEFAULT_RETRY_OPTIONS, ...retryOptions }
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      addBreadcrumb(`API call attempt ${attempt + 1}`, 'http', { url })

      const response = await fetch(url, options)

      // Check if response status is retryable
      if (!response.ok && opts.retryableStatuses.includes(response.status)) {
        if (attempt < opts.maxRetries) {
          const delay = calculateDelay(attempt, opts.baseDelayMs, opts.maxDelayMs)
          if (process.env.NODE_ENV === 'development') {
            console.log(
              `Request to ${url} failed with status ${response.status}, retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${opts.maxRetries})`
            )
          }
          await sleep(delay)
          continue
        }
      }

      return response
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))

      if (isRetryableError(error) && attempt < opts.maxRetries) {
        const delay = calculateDelay(attempt, opts.baseDelayMs, opts.maxDelayMs)
        if (process.env.NODE_ENV === 'development') {
          console.log(
            `Request to ${url} failed with error, retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${opts.maxRetries}):`,
            error
          )
        }
        await sleep(delay)
        continue
      }

      // Log to Sentry on final failure
      if (attempt === opts.maxRetries) {
        captureError(lastError, {
          url,
          attempts: attempt + 1,
          lastAttempt: true,
        })
      }

      throw error
    }
  }

  // This should never be reached, but TypeScript needs it
  throw lastError || new Error('Retry failed')
}

/**
 * Wrapper for JSON API calls with retry
 */
export async function apiCall<T>(
  url: string,
  options?: RequestInit,
  retryOptions?: RetryOptions
): Promise<T> {
  const response = await fetchWithRetry(url, options, retryOptions)

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error')
    const error = new Error(`API error ${response.status}: ${errorText}`)
    captureError(error, { url, status: response.status })
    throw error
  }

  return response.json()
}

/**
 * Get the Authorization header with the current user's Firebase ID token.
 * Returns an empty object if no user is signed in.
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser
  if (!user) return {}
  try {
    const token = await user.getIdToken()
    return { Authorization: `Bearer ${token}` }
  } catch {
    return {}
  }
}

/**
 * POST JSON with retry
 */
export async function postJson<T>(
  url: string,
  data: unknown,
  retryOptions?: RetryOptions
): Promise<T> {
  return apiCall<T>(
    url,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    },
    retryOptions
  )
}
